import { ArrayHelpers, Field, FieldArray, getIn } from 'formik';
import React from 'react';
import Stack, { contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import { getSpacing } from '../../../../../utils/spacing';
import Text from '../../../../v2BaseComponents/Text';
import { getIntl } from '../../../../../utils/intl';
import IconButton from '../../../../v2BaseComponents/IconButton';
import { useIsDisabledByParent } from '../../../../v2BaseComponents/IsDisabledProvider';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import { required } from '../../../../../utils/validators';
import Divider from '../../../../v2BaseComponents/Divider';
import { InvitationDefinitionFileUpsert } from '../../../../../primary-record-types/apiTypes/organizationService';
import Icon from '../../../../v2BaseComponents/Icon';
import Button from '../../../../v2BaseComponents/Button';
import { useTempFilePicker } from '../../../../v2BaseComponents/ImagePicker/TempFilePicker';
import { FormikFieldProps } from '../../../../v2BaseComponents/Inputs/FormikInputs';
import FormInputSection from '../FormInputSection';

const intl = getIntl('invitation-definition-form-fields-checkboxes', {
    consentFiles: 'Consent Files',
    consentFileLabel: 'Consent File #{index}',
    name: 'Name',
    description: 'Description',
    removeFile: 'Remove File',
    uploadFile: 'Upload File',
});

export type FilesFieldOwnProps = {
    isDisabled: boolean;
    values: InvitationDefinitionFileUpsert[];
};

export type FilesFieldProps = FilesFieldOwnProps;

const ExistingFileDisplay: React.FC<{ onClear: () => void; isDisabled: boolean; index: number }> = (props) => (
    <Stack vertical fullWidth justify="center" align="center">
        <Icon name="FileText" size="selected-file-icon" testID="file-picker-display" />
        <Button
            variant="text-dark"
            onPress={props.onClear}
            isDisabled={props.isDisabled}
            title={intl('removeFile')}
            icon="Trash2"
            testID={`delete-file-${props.index}`}
        />
    </Stack>
);

const UploadFileInput: React.FC<{ onUpload: (tempFileId: string) => void; isDisabled: boolean; error?: string }> = (
    props,
) => {
    const { isLoadingFile, onPress, E2EInput } = useTempFilePicker({
        onPick: (value) => {
            if (value?.tempFileId) {
                props.onUpload(value.tempFileId);
            }
        },
    });

    return (
        <Stack vertical fullWidth justify="center" align="center">
            {E2EInput}
            <Button
                variant="secondary"
                onPress={onPress}
                isDisabled={props.isDisabled || isLoadingFile}
                title={intl('uploadFile')}
                icon="Upload"
            />
            {props.error ? (
                <Text variant="input-error" style={{ marginTop: getSpacing(1) }}>
                    {props.error}
                </Text>
            ) : null}
        </Stack>
    );
};

const FormikFieldUploadFileInput: React.FC<FormikFieldProps<{}> & { isDisabled: boolean }> = (props) => {
    const validationError = getIn(props.form.errors, props.field.name);
    const touched = getIn(props.form.touched, props.field.name);
    const error = touched && validationError ? validationError : null;

    return (
        <UploadFileInput
            onUpload={(tempFileId) => {
                props.form.setFieldValue(props.field.name, tempFileId);
            }}
            isDisabled={props.isDisabled}
            error={error}
        />
    );
};

const FileField: React.FC<{
    index: number;
    onRemove: () => void;
    isDisabled: boolean;
    value: InvitationDefinitionFileUpsert;
    onChange: (value: InvitationDefinitionFileUpsert) => void;
}> = (props) => {
    return (
        <FormInputSection
            title={intl('consentFileLabel', { index: props.index + 1 })}
            onRemove={props.onRemove}
            isDisabled={props.isDisabled}
            removeTestID={`remove-file-${props.index}`}
        >
            {props.value.fileId || props.value.tempFileId ? (
                <ExistingFileDisplay
                    onClear={() =>
                        props.onChange({
                            ...props.value,
                            tempFileId: '',
                            fileId: '',
                        })
                    }
                    isDisabled={props.isDisabled}
                    index={props.index}
                />
            ) : (
                <Field
                    name={`files.${props.index}.tempFileId`}
                    component={FormikFieldUploadFileInput}
                    isDisabled={props.isDisabled}
                    validate={required}
                    isRequired
                />
            )}
            <Field
                name={`files.${props.index}.name`}
                component={FormikInputs.Input}
                isRequired
                validate={required}
                title={intl('name')}
            />
            <Field
                name={`files.${props.index}.description`}
                component={FormikInputs.Input}
                isRequired
                validate={required}
                title={intl('description')}
            />
        </FormInputSection>
    );
};

const FilesField: React.FC<FilesFieldProps> = (props) => {
    const isDisabled = useIsDisabledByParent(props.isDisabled);
    return (
        <FieldArray name="files">
            {(arrayHelpers: ArrayHelpers<InvitationDefinitionFileUpsert[]>) => (
                <Stack
                    vertical
                    space={contentStackSpacing}
                    fullWidth
                    style={{ marginTop: getSpacing(2.5), marginBottom: getSpacing(2.5) }}
                >
                    <Stack horizontal justify={'space-between'} align="center" fullWidth>
                        <Text variant="h5">{intl('consentFiles')}</Text>
                        <IconButton
                            testID="add-file-button"
                            icon="Plus"
                            variant="transparent"
                            onPress={() => {
                                const newValue: InvitationDefinitionFileUpsert = {
                                    name: '',
                                    description: '',
                                    tempFileId: '',
                                };
                                arrayHelpers.push(newValue);
                            }}
                            isDisabled={isDisabled}
                        />
                    </Stack>
                    {props.values.map((image, index) => (
                        <FileField
                            key={index}
                            onRemove={() => arrayHelpers.remove(index)}
                            index={index}
                            isDisabled={props.isDisabled}
                            value={image}
                            onChange={(value) => {
                                arrayHelpers.replace(index, value);
                            }}
                        />
                    ))}
                    {props.values.length ? <Divider horizontal /> : null}
                </Stack>
            )}
        </FieldArray>
    );
};

export default React.memo(FilesField);
